import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Message from 'src/views/management/ChatView/ChatDetails/Message';
import LoadMoreButton from 'src/views/management/ChatView/styled/LoadMoreButton';
import ContactChatListItem from 'src/views/management/ChatView/ChatSidebar/styled/ContactChatListItem';
import { getLastMessageInfo, getLastMessageTime } from 'src/views/management/ChatView/ChatSidebar/ChatList/ChatItem';
import { getShortCodeThread, markShortCodeRead } from 'src/actions/shortCodeActions';
import { ROUTES } from 'src/constants';
import {
  SHORT_CODES_PATH,
  SHORT_CODE_NO_REPLY_NOTE,
  shortCodeThreadPath,
} from 'src/views/management/ChatView/ShortCodes/shortCodePaths';

/**
 * CON-2014 — the right-hand side of Connections for short codes: with no code
 * in the URL, the list of codes; with one, that code's read-only thread.
 * There is no message box: CP cannot text a short code.
 */

function Header({ title, backTo }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: { xs: '12px 6px', md: '16px 36px' },
        minHeight: '64px',
      }}
    >
      <IconButton component={RouterLink} to={backTo} size="small" aria-label="Back">
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="h3" color="textPrimary">
        {title}
      </Typography>
    </Box>
  );
}

Header.propTypes = {
  title: PropTypes.string.isRequired,
  backTo: PropTypes.string.isRequired,
};

function NoReplyNote() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: { xs: '12px 16px', md: '16px 36px' },
        padding: '12px 16px',
        borderRadius: '8px',
        backgroundColor: '#f5f5f5',
        color: '#546E7A',
      }}
    >
      <InfoOutlinedIcon fontSize="small" />
      <Typography variant="body2">
        {SHORT_CODE_NO_REPLY_NOTE}
      </Typography>
    </Box>
  );
}

function ShortCodeList() {
  const { shortCodes = [], isLoaded } = useSelector((state) => state?.shortCodes || {});

  return (
    <>
      <Header title="Short codes" backTo={ROUTES.APP_MANAGEMENT_CHAT} />
      <Divider />
      <Box component={PerfectScrollbar} options={{ suppressScrollX: true }} sx={{ padding: { xs: '16px', md: '24px 36px' } }}>
        {isLoaded && !shortCodes.length && (
          <Typography color="textSecondary">No texts from short codes</Typography>
        )}
        <List>
          {shortCodes.map((item) => (
            <ContactChatListItem
              key={item.code}
              component={RouterLink}
              to={shortCodeThreadPath(item.code)}
            >
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '6px'
              }}
              >
                <Typography variant="h5" color="textPrimary">{item.code}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {item.latestReceivedAt ? getLastMessageTime(item.latestReceivedAt) : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography variant="body2" sx={{ color: '#546E7A', paddingRight: '4px' }} noWrap>
                  {getLastMessageInfo(item.lastMessage)}
                </Typography>
                <Badge
                  badgeContent={item.unreadCount}
                  invisible={!item.unreadCount}
                  color="secondary"
                  sx={{ marginLeft: '30px', '& .MuiBadge-badge': { right: 10, top: 10 } }}
                />
              </Box>
            </ContactChatListItem>
          ))}
        </List>
      </Box>
    </>
  );
}

function ShortCodeThread({ code }) {
  const dispatch = useDispatch();
  const messagesRef = useRef(null);
  const thread = useSelector((state) => state?.shortCodes?.threads?.[code]);
  const messages = useMemo(() => thread?.messages || [], [thread?.messages]);
  const firstNewIndex = messages.findIndex((m) => !m.isEarlierHistory);

  useEffect(() => {
    dispatch(getShortCodeThread(code));
  }, [code]);

  // Opening the thread reads it. Earlier texts to an Unnamed Contact are
  // history only and are read on that contact's chat, not here.
  useEffect(() => {
    const unreadIds = messages
      .filter((m) => m.isReceived && !m.readAt && !m.isEarlierHistory)
      .map((m) => m.id);
    if (unreadIds.length) {
      dispatch(markShortCodeRead(code, unreadIds));
    }
  }, [messages, code]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages.at(-1)?.id]);

  return (
    <>
      <Header title={`${code} (short code)`} backTo={SHORT_CODES_PATH} />
      <Divider />
      <Box
        component={PerfectScrollbar}
        options={{ suppressScrollX: true }}
        containerRef={(container) => { messagesRef.current = container; }}
        sx={{ flexGrow: 1, padding: { xs: '24px 16px', md: '24px 36px' } }}
      >
        {thread?.nextCursor && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LoadMoreButton
              startIcon={<KeyboardArrowUpIcon />}
              disabled={thread?.isLoading}
              onClick={() => dispatch(getShortCodeThread(code, thread.nextCursor))}
              sx={{ marginBottom: '24px' }}
            >
              Load previous messages
            </LoadMoreButton>
          </Box>
        )}
        {messages.map((message, index) => (
          <React.Fragment key={message.id}>
            {index === 0 && message.isEarlierHistory && (
              <Typography variant="caption" color="textSecondary" component="p" sx={{ textAlign: 'center', marginBottom: '12px' }}>
                Earlier texts from this short code
              </Typography>
            )}
            {index === firstNewIndex && index > 0 && (
              <Divider sx={{ marginBottom: '16px' }} />
            )}
            <Message message={message} showPhoneNumber={false} userCanDeleteTexts={false} />
          </React.Fragment>
        ))}
      </Box>
      <NoReplyNote />
    </>
  );
}

ShortCodeThread.propTypes = {
  code: PropTypes.string.isRequired,
};

function ShortCodes({ code = null }) {
  return (
    <Box sx={{
      flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0
    }}
    >
      {code ? <ShortCodeThread code={code} /> : <ShortCodeList />}
    </Box>
  );
}

ShortCodes.propTypes = {
  code: PropTypes.string,
};

export default ShortCodes;
